import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import ReactEDSWrapper from '../utils/react-eds-wrapper';
import secureLocalStorage from '../utils/secureStorage';
import {
  SystemBar,
  SystemBarComponent,
  AppContainer,
  AppBar,
  AppBody,
  NavItem,
  Navigation,
  NavGroup,
  NotificationTrigger,
  NotificationLogPanel,
  NotificationLogItem
} from '@dsi/react-eds';
import { useEffect, useState } from 'react';
import { useTheme } from './useTheme';
import { UseBreadcrumb } from '../const/Breadcrumb';
import { useSwobNotifications } from './NotificationsProvider';
import SystemPanel from '../components/UserPanel/SystemPanel';
import { UserRoles } from '../const/UserManagementTypes';
import { formatLastLoginTime, isValidDate } from '../utils/dateUtils';
import { IS_SHOW_TRACEHUNT, HIDE_NIA_ADMINISTRATION } from '../config';
import { hasNiaPermission, NIA_PERMISSIONS } from '../const/niaPermissions';
import { CircularProgress, Box } from '@mui/material';

const ProtectedRoute = () => {
  const [loaded, setLoaded] = useState(false);
  const location = useLocation();
  const { authenticated, loading, user, token, logout, hasRole, lastLoginAt, menuPermissions } = useAuth();
  const [silentMode, setSilentMode] = useState(() => {
    const stored = localStorage.getItem('silentMode');
    return stored ? JSON.parse(stored) : false;
  });
  const [showSystemPanel, setShowSystemPanel] = useState(false);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [hasShownLoginNotification, setHasShownLoginNotification] = useState(() => {
    return secureLocalStorage.getItem('hasShownLoginNotification') === 'true';
  });
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    localStorage.setItem('silentMode', JSON.stringify(silentMode));
  }, [silentMode]);

  const [currentPath, setCurrentPath] = useState('');

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  // When true, the NIA Administration menu group is hidden (managed via Keycloak now)
  const hideNiaAdministration = HIDE_NIA_ADMINISTRATION === 'true';

  const isShowTraceHunt = IS_SHOW_TRACEHUNT === 'true';

  const getTicket = (path: string): string => {
    const paths = path.split('/');
    return paths[paths.length - 2] || '';
  };

  // Extract dynamic segments from NII paths
  const getNiiRunId = (path: string): string => {
    // /nii/audit-reports/run/{runId} or /nii/audit-reports/run/{runId}/{reportId}
    const parts = path.split('/');
    const runIdx = parts.indexOf('run');
    return runIdx >= 0 && parts[runIdx + 1] ? parts[runIdx + 1] : '';
  };

  const getNiiGplVersion = (path: string): string => {
    // /nii/audit-reports/release/{gplVersion} or /nii/audit-reports/release/{gplVersion}/{reportId}
    const parts = path.split('/');
    const releaseIdx = parts.indexOf('release');
    return releaseIdx >= 0 && parts[releaseIdx + 1] ? decodeURIComponent(parts[releaseIdx + 1]) : '';
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pathToTitleMap: { [s: string]: any } = {
    '/home': 'Home',
    '/integrations/sw-repositories/ericsson-sw-gateway': UseBreadcrumb([
      { title: 'SW Repositories', url: '' },
      { title: 'Ericsson SW Gateway', url: '' }
    ]),
    '/integrations/sw-repositories/external-repositories': UseBreadcrumb([
      { title: 'Integrations', url: '' },
      { title: 'SW Repositories', url: '' },
      { title: 'External Repositories', url: '' }
    ]),
    '/integrations/eiap-clusters/edit': UseBreadcrumb([
      { title: 'Integrations', url: '' },
      { title: 'EIAP Clusters', url: '/integrations/eiap-clusters' },
      { title: 'Edit EIAP Cluster', url: '' }
    ]),
    '/integrations/eiap-clusters/new': UseBreadcrumb([
      { title: 'Integrations', url: '' },
      { title: 'EIAP Clusters', url: '/integrations/eiap-clusters' },
      { title: 'Create New EIAP Cluster', url: '' }
    ]),
    '/integrations/eiap-clusters': UseBreadcrumb([
      { title: 'Integrations', url: '' },
      { title: 'EIAP Clusters', url: '' }
    ]),
    '/integrations/enms/edit': UseBreadcrumb([
      { title: 'Integrations', url: '' },
      { title: 'ENMS', url: '/integrations/enms' },
      { title: 'Edit ENM', url: '' }
    ]),
    '/integrations/enms/new': UseBreadcrumb([
      { title: 'Integrations', url: '' },
      { title: 'ENMS', url: '/integrations/enms' },
      { title: 'Create New ENM', url: '' }
    ]),
    '/integrations/enms': UseBreadcrumb([
      { title: 'Integrations', url: '' },
      { title: 'ENMS', url: '' }
    ]),
    '/integrations/test-automation': UseBreadcrumb([
      { title: 'Integrations', url: '' },
      { title: 'Test Automation', url: '' }
    ]),
    '/integrations/cnis/new': UseBreadcrumb([
      { title: 'Integrations', url: '' },
      { title: 'CNIS', url: '/integrations/cnis' },
      { title: 'Create CNIS Endpoint', url: '' }
    ]),
    '/integrations/cnis/edit': UseBreadcrumb([
      { title: 'Integrations', url: '' },
      { title: 'CNIS', url: '/integrations/cnis' },
      { title: 'Edit CNIS Endpoint', url: '' }
    ]),
    '/integrations/cnis': UseBreadcrumb([
      { title: 'Integrations', url: '' },
      { title: 'CNIS', url: '' }
    ]),
    '/integrations/moshell/new': UseBreadcrumb([
      { title: 'Integrations', url: '' },
      { title: 'Moshell Server', url: '' },
      { title: 'Create Moshell Endpoint', url: '' }
    ]),
    '/integrations/moshell/edit': UseBreadcrumb([
      { title: 'Integrations', url: '' },
      { title: 'Moshell Server', url: '' },
      { title: 'Edit Moshell Endpoint', url: '' }
    ]),
    '/integrations/moshell': UseBreadcrumb([
      { title: 'Integrations', url: '' },
      { title: 'Moshell Server', url: '' }
    ]),
    '/automation-flows/test-orchestration/executions': UseBreadcrumb([
      { title: 'Automation Workflows', url: '' },
      { title: 'Test Orchestration', url: '' },
      { title: 'Executions', url: '' }
    ]),
    '/automation-flows/test-orchestration/management': UseBreadcrumb([
      { title: 'Automation Workflows', url: '' },
      { title: 'Test Orchestration', url: '' },
      { title: 'Management', url: '' }
    ]),
    '/automation-flows/workflows': UseBreadcrumb([
      { title: 'Automation Workflows', url: '' },
      { title: 'Executions', url: '' }
    ]),
    '/automation-flows/gnb-nodes': UseBreadcrumb([
      { title: 'Automation Workflows', url: '' },
      { title: 'CR gNB Node Management', url: '' }
    ]),
    '/automation-flows/new': UseBreadcrumb([
      { title: 'Automation Workflows', url: '' },
      { title: 'Configuration', url: '/automation-flows' },
      { title: 'Create Configuration Instance', url: '' }
    ]),
    '/automation-flows/dashboard/overview': UseBreadcrumb([
      { title: 'Automation Workflows', url: '' },
      { title: 'Dashboard', url: '' },
      { title: 'Overview', url: '' }
    ]),
    '/automation-flows/dashboard/trends': UseBreadcrumb([
      { title: 'Automation Workflows', url: '' },
      { title: 'Dashboard', url: '' },
      { title: 'Trends', url: '' }
    ]),
    '/automation-flows/dashboard/test-automation': UseBreadcrumb([
      { title: 'Automation Workflows', url: '' },
      { title: 'Dashboard', url: '' },
      { title: 'Test Automation', url: '' }
    ]),
    '/automation-flows/(?:(?!new).)(.+)/(.+)$': UseBreadcrumb([
      { title: 'Automation Workflows', url: '' },
      { title: 'Executions', url: '/automation-flows/workflows' },
      { title: getTicket(currentPath), url: '' }
    ]),
    '/automation-flows': UseBreadcrumb([
      { title: 'Automation Workflows', url: '' },
      { title: 'Configuration', url: '' }
    ]),
    '/settings': UseBreadcrumb([{ title: 'Settings', url: '' }]),
    '/nii/impact-reports/feature/.+': UseBreadcrumb([
      { title: 'Network Impact Intelligence', url: '/nii/executions' },
      { title: 'Impact Reports', url: '/nii/impact-reports' },
      { title: 'Feature Impact', url: '' }
    ]),
    '/nii/impact-reports/tr/.+': UseBreadcrumb([
      { title: 'Network Impact Intelligence', url: '/nii/executions' },
      { title: 'Impact Reports', url: '/nii/impact-reports' },
      { title: 'Trouble Report Impact', url: '' }
    ]),
    '/nii/impact-reports$': UseBreadcrumb([
      { title: 'Network Impact Intelligence', url: '/nii/executions' },
      { title: 'Impact Reports', url: '' }
    ]),
    '/nii/executions': UseBreadcrumb([
      { title: 'Network Impact Intelligence', url: '/nii/executions' },
      { title: 'Executions', url: '' }
    ]),
    '/nii/script-management': UseBreadcrumb([
      { title: 'Network Impact Intelligence', url: '/nii/executions' },
      { title: 'Script Management', url: '' }
    ]),
    '/nii/audit-reports/new': UseBreadcrumb([
      { title: 'Network Impact Intelligence', url: '/nii/executions' },
      { title: 'Audit Reports', url: '/nii/audit-reports' },
      { title: 'New Audit', url: '' }
    ]),
    '/nii/audit-reports/release/[^/]+/[^/]+': UseBreadcrumb([
      { title: 'Network Impact Intelligence', url: '/nii/executions' },
      { title: 'Audit Reports', url: '/nii/audit-reports' },
      { title: getNiiGplVersion(currentPath), url: `/nii/audit-reports/release/${encodeURIComponent(getNiiGplVersion(currentPath))}` },
      { title: 'Node Report', url: '' }
    ]),
    '/nii/audit-reports/release/.+': UseBreadcrumb([
      { title: 'Network Impact Intelligence', url: '/nii/executions' },
      { title: 'Audit Reports', url: '/nii/audit-reports' },
      { title: getNiiGplVersion(currentPath), url: '' }
    ]),
    '/nii/audit-reports/run/[^/]+/[^/]+': UseBreadcrumb([
      { title: 'Network Impact Intelligence', url: '/nii/executions' },
      { title: 'Executions', url: '/nii/executions' },
      { title: 'Audit Detail', url: `/nii/audit-reports/run/${getNiiRunId(currentPath)}` },
      { title: 'Node Report', url: '' }
    ]),
    '/nii/audit-reports/run/.+': UseBreadcrumb([
      { title: 'Network Impact Intelligence', url: '/nii/executions' },
      { title: 'Executions', url: '/nii/executions' },
      { title: 'Audit Detail', url: '' }
    ]),
    '/nii/audit-reports$': UseBreadcrumb([
      { title: 'Network Impact Intelligence', url: '/nii/executions' },
      { title: 'Audit Reports', url: '' }
    ]),
    '/anomaly-dashboards/kpi-viewer': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Dashboards', url: '/anomaly-dashboards' },
      { title: 'KPI Viewer', url: '' }
    ]),
    '/anomaly-dashboards/swchange-dashboard': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Dashboards', url: '/anomaly-dashboards' },
      { title: 'SW Change Dashboard', url: '' }
    ]),
    '/anomaly-dashboards/cd-monitoring-viewer': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Dashboards', url: '/anomaly-dashboards' },
      { title: 'Pilot Monitoring Viewer', url: '' }
    ]),
    '/anomaly-dashboards': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Dashboards', url: '' }
    ]),
    '/use-cases/new/kpi-deviation-detection': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'New Use Case', url: '/use-cases/new' },
      { title: 'KPI Deviation Detection', url: '' }
    ]),
    '/use-cases/new/missing-pm-data': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'New Use Case', url: '/use-cases/new' },
      { title: 'Missing PM Data', url: '' }
    ]),
    '/use-cases/new/kpi-deterioration-detection': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'New Use Case', url: '/use-cases/new' },
      { title: 'Identify the nodes with KPI deterioration', url: '' }
    ]),
    '/use-cases/new/conf-change-alert': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'New Use Case', url: '/use-cases/new' },
      { title: 'Configuration Change Alert', url: '' }
    ]),
    '/use-cases/new/feature-changes-detection': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'New Use Case', url: '/use-cases/new' },
      { title: 'Feature Changes Detection', url: '' }
    ]),
    '/use-cases/new/sw-change': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'New Use Case', url: '/use-cases/new' },
      { title: 'Robust Detection of SW Changes', url: '' }
    ]),
    '/use-cases/new/new-alarms-after-sw-change': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'New Use Case', url: '/use-cases/new' },
      { title: 'New Alarms after SW Change', url: '' }
    ]),
    '/use-cases/new/non-default-system-constants-collection': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'New Use Case', url: '/use-cases/new' },
      { title: 'Non-Default System Constants Collection', url: '' }
    ]),
    '/use-cases/new/daily-aggregated-kpi-checks': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'New Use Case', url: '/use-cases/new' },
      { title: 'Daily Aggregated KPI Checks', url: '' }
    ]),
    '/use-cases/edit/kpi-deviation-detection': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'KPI Deviation Detection', url: '' }
    ]),
    '/use-cases/edit/missing-pm-data': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'Missing PM Data', url: '' }
    ]),
    '/use-cases/edit/kpi-deterioration-detection': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'Identify the nodes with KPI deterioration', url: '' }
    ]),
    '/use-cases/edit/conf-change-alert': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'Configuration Change Alert', url: '' }
    ]),
    '/use-cases/edit/feature-changes-detection': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'Feature Changes Detection', url: '' }
    ]),
    '/use-cases/edit/sw-change': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'Robust Detection of SW Changes', url: '' }
    ]),
    '/use-cases/edit/new-alarms-after-sw-change': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'New Alarms after SW Change', url: '' }
    ]),
    '/use-cases/edit/non-default-system-constants-collection': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'Non-Default System Constants Collection', url: '' }
    ]),
    '/use-cases/edit/daily-aggregated-kpi-checks': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'Daily Aggregated KPI Checks', url: '' }
    ]),
    '/use-cases/new': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '/use-cases/instances' },
      { title: 'New Use Case', url: '' }
    ]),
    '/use-cases/instances': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Instances', url: '' }
    ]),
    '/use-cases/executions': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Executions', url: '' }
    ]),
    '/use-cases/reporters': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '/use-cases' },
      { title: 'Reporters', url: '' }
    ]),
    '/use-cases': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Use Cases', url: '' }
    ]),
    '/dcs-settings/diagnostics': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Settings', url: '/dcs-settings' },
      { title: 'Diagnostics', url: '' }
    ]),
    '/dcs-settings/enm-portal-access': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Settings', url: '/dcs-settings' },
      { title: 'ENM Portal Access', url: '' }
    ]),
    '/dcs-settings/enm-ssh-access': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Settings', url: '/dcs-settings' },
      { title: 'ENM SSH Access', url: '' }
    ]),
    '/dcs-settings/node-collection-settings': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Settings', url: '/dcs-settings' },
      { title: 'Node Collection Settings', url: '' }
    ]),
    '/dcs-settings/cluster-management': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Settings', url: '/dcs-settings' },
      { title: 'Node Cluster Management', url: '' }
    ]),
    '/dcs-settings': UseBreadcrumb([
      { title: 'KPI Analytics', url: '' },
      { title: 'Settings', url: '' }
    ]),
    '/tracehunt': UseBreadcrumb([{ title: 'TraceHunt', url: '' }])
  };

  const { getNotifications, getNewNotificationCount, acknowledgeNotification, readNotification, addNotifications } = useSwobNotifications();
  const notifications = getNotifications();
  const newNotifications = getNewNotificationCount();

  useEffect(() => {
    setLoaded(true);
  }, [loaded]);

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location, setCurrentPath]);

  // Show login notification when user first enters protected route
  useEffect(() => {
    if (token && user?.displayName && !hasShownLoginNotification) {
      if (lastLoginAt && isValidDate(lastLoginAt)) {
        addNotifications([
          {
            title: `Welcome back, ${user.displayName}!`,
            description: `Your last login was on ${formatLastLoginTime(lastLoginAt)}`,
            timestamp: new Date(),
            color: 'green',
            icon: 'icon-success'
          }
        ]);
      } else {
        addNotifications([
          {
            title: `Welcome, ${user.displayName}!`,
            description: 'This is your first login to the system.',
            timestamp: new Date(),
            color: 'green',
            icon: 'icon-success'
          }
        ]);
      }
      setHasShownLoginNotification(true);
      secureLocalStorage.setItem('hasShownLoginNotification', 'true');
    }
  }, [token, user?.username, user?.displayName, lastLoginAt, hasShownLoginNotification, addNotifications]);

  useEffect(() => {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 300);
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let layout: any = null;
    const timer = setTimeout(() => {
      import('@eds/vanilla')
        .then(({ Layout }) => {
          const body = document.querySelector('body');
          if (body?.querySelector('.app')) {
            layout = new Layout(body);
            layout.init();
          }
        })
        .catch(() => {});
    }, 500);
    return () => {
      clearTimeout(timer);
      if (layout) layout.destroy();
    };
  }, [authenticated]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  const navigation = (
    <ReactEDSWrapper component={Navigation}>
      <ReactEDSWrapper component={NavItem} active={currentPath === '/home' || currentPath === '/'}>
        <Link to="/home">Home</Link>
      </ReactEDSWrapper>

      {/* Integration */}
      <ReactEDSWrapper component={NavGroup} title="Integrations" active={currentPath.startsWith('/integrations')}>
        {/* SW Repositories */}
        <ReactEDSWrapper component={NavGroup} title="SW Repositories" active={currentPath.includes('/sw-repositories')}>
          <ReactEDSWrapper component={NavItem} active={currentPath === '/integrations/sw-repositories/ericsson-sw-gateway'}>
            <Link to="/integrations/sw-repositories/ericsson-sw-gateway">
              <span>Ericsson SW Gateway</span>
            </Link>
          </ReactEDSWrapper>
          <ReactEDSWrapper component={NavItem} active={currentPath === '/integrations/sw-repositories/external-repositories'}>
            <Link to="/integrations/sw-repositories/external-repositories">
              <span>External Repositories</span>
            </Link>
          </ReactEDSWrapper>
        </ReactEDSWrapper>
        <ReactEDSWrapper component={NavItem} active={currentPath.includes('/integrations/eiap-clusters')}>
          <Link to="/integrations/eiap-clusters">
            <span>EIAP Clusters</span>
          </Link>
        </ReactEDSWrapper>
        <ReactEDSWrapper component={NavItem} active={currentPath.includes('/integrations/enms')}>
          <Link to="/integrations/enms">
            <span>ENMs</span>
          </Link>
        </ReactEDSWrapper>
        <ReactEDSWrapper component={NavItem} active={currentPath.includes('/integrations/cnis')}>
          <Link to="/integrations/cnis">
            <span>CNIS</span>
          </Link>
        </ReactEDSWrapper>
        <ReactEDSWrapper component={NavItem} active={currentPath.includes('/integrations/moshell')}>
          <Link to="/integrations/moshell">
            <span>Moshell Server</span>
          </Link>
        </ReactEDSWrapper>
        <ReactEDSWrapper component={NavItem} active={currentPath.includes('/integrations/test-automation')}>
          <Link to="/integrations/test-automation">
            <span>Test Automation</span>
          </Link>
        </ReactEDSWrapper>
      </ReactEDSWrapper>

      {/* Automation Flows */}
      <ReactEDSWrapper
        component={NavGroup}
        title="Automation Workflows"
        style={menuPermissions['showSwob'] ? {} : { display: 'none' }}
        active={currentPath.startsWith('/automation-flows')}
      >
        {/* Dashboard */}
        <ReactEDSWrapper component={NavGroup} title="Dashboards" active={currentPath.includes('/dashboard')}>
          <ReactEDSWrapper component={NavItem} active={currentPath === '/automation-flows/dashboard/overview'}>
            <Link to="/automation-flows/dashboard/overview">
              <span>Overview</span>
            </Link>
          </ReactEDSWrapper>
          {/* commented for later use
            <ReactEDSWrapper component={NavItem} active={currentPath === '/automation-flows/dashboard/trends'}>
            <Link to="/automation-flows/dashboard/trends">
              <span>Trends</span>
            </Link>
          </ReactEDSWrapper> */}
          <ReactEDSWrapper component={NavItem} active={currentPath === '/automation-flows/dashboard/test-automation'}>
            <Link to="/automation-flows/dashboard/test-automation">
              <span>Test Automation</span>
            </Link>
          </ReactEDSWrapper>
        </ReactEDSWrapper>
        {/* Executions */}
        <ReactEDSWrapper
          component={NavItem}
          active={
            (currentPath === '/automation-flows/workflows' || /\/automation-flows\/(?:(?!new).)(.+)\/(.+)$/.test(currentPath)) &&
            !currentPath.includes('dashboard') &&
            !currentPath.includes('test-orchestration')
          }
        >
          <Link to="/automation-flows/workflows">
            <span>Executions</span>
          </Link>
        </ReactEDSWrapper>
        {/* Configuration */}
        <ReactEDSWrapper
          component={NavItem}
          active={currentPath === '/automation-flows' || /\/automation-flows\/new(\/(.+))*$/.test(currentPath)}
        >
          <Link to="/automation-flows">
            <span>Create Workflow</span>
          </Link>
        </ReactEDSWrapper>
        {/* CR gNB Node Management */}
        <ReactEDSWrapper component={NavItem} active={currentPath === '/automation-flows/gnb-nodes'}>
          <Link to="/automation-flows/gnb-nodes">
            <span>CR gNB Node Management</span>
          </Link>
        </ReactEDSWrapper>
        {/* Test Orchestration */}
        <ReactEDSWrapper component={NavGroup} title="Test Orchestration" active={currentPath.includes('/test-orchestration')}>
          <ReactEDSWrapper component={NavItem} active={currentPath === '/automation-flows/test-orchestration/management'}>
            <Link to="/automation-flows/test-orchestration/management">
              <span>Test Case Management</span>
            </Link>
          </ReactEDSWrapper>
          <ReactEDSWrapper component={NavItem} active={currentPath.includes('/automation-flows/test-orchestration/executions')}>
            <Link to="/automation-flows/test-orchestration/executions">
              <span>Executions</span>
            </Link>
          </ReactEDSWrapper>
        </ReactEDSWrapper>
      </ReactEDSWrapper>

      {/* Network Impact Intelligence */}
      <ReactEDSWrapper
        component={NavGroup}
        title="Network Impact Intelligence"
        style={menuPermissions['showNii'] ? {} : { display: 'none' }}
        active={currentPath.startsWith('/nii')}
      >
        <ReactEDSWrapper
          component={NavItem}
          active={currentPath === '/nii/executions' || currentPath.startsWith('/nii/audit-reports/run/')}
        >
          <Link to="/nii/executions">
            <span>Executions</span>
          </Link>
        </ReactEDSWrapper>
        <ReactEDSWrapper component={NavItem} active={currentPath.startsWith('/nii/impact-reports')}>
          <Link to="/nii/impact-reports" onClick={() => sessionStorage.removeItem('nii-selected-release')}>
            <span>Impact Reports</span>
          </Link>
        </ReactEDSWrapper>
        <ReactEDSWrapper
          component={NavItem}
          active={currentPath.startsWith('/nii/audit-reports') && !currentPath.startsWith('/nii/audit-reports/run/')}
        >
          <Link to="/nii/audit-reports">
            <span>Audit Reports</span>
          </Link>
        </ReactEDSWrapper>
        <ReactEDSWrapper component={NavItem} active={currentPath === '/nii/script-management'}>
          <Link to="/nii/script-management">
            <span>Script Management</span>
          </Link>
        </ReactEDSWrapper>
      </ReactEDSWrapper>

      {/* KPI Analytics */}
      <ReactEDSWrapper
        component={NavGroup}
        title="KPI Analytics"
        style={menuPermissions['showKpi'] ? {} : { display: 'none' }}
        active={
          currentPath.startsWith('/usecases') ||
          currentPath.startsWith('/new-usecase') ||
          currentPath.startsWith('/anomaly-dashboard') ||
          currentPath.startsWith('/use-cases') ||
          currentPath.startsWith('/anomaly-dashboards') ||
          currentPath.startsWith('/dcs-settings') ||
          currentPath.startsWith('/advanced-config')
        }
      >
        <ReactEDSWrapper component={NavGroup} title="Use Cases" active={currentPath.startsWith('/use-cases')}>
          <ReactEDSWrapper
            component={NavItem}
            active={
              currentPath.startsWith('/use-cases/instances') ||
              currentPath.startsWith('/use-cases/new') ||
              currentPath.startsWith('/use-cases/edit')
            }
          >
            <Link to="/use-cases/instances">
              <span>Instances</span>
            </Link>
          </ReactEDSWrapper>
          <ReactEDSWrapper component={NavItem} active={currentPath.startsWith('/use-cases/executions')}>
            <Link to="/use-cases/executions">
              <span>Executions</span>
            </Link>
          </ReactEDSWrapper>
          <ReactEDSWrapper component={NavItem} active={currentPath.startsWith('/use-cases/reporters')}>
            <Link to="/use-cases/reporters">
              <span>Reporters</span>
            </Link>
          </ReactEDSWrapper>
        </ReactEDSWrapper>
        <ReactEDSWrapper component={NavGroup} title="Dashboards" active={currentPath.startsWith('/anomaly-dashboard')}>
          <ReactEDSWrapper component={NavItem} active={currentPath === '/anomaly-dashboards/kpi-viewer'}>
            <Link to="/anomaly-dashboards/kpi-viewer">
              <span>KPI Viewer</span>
            </Link>
          </ReactEDSWrapper>
          <ReactEDSWrapper component={NavItem} active={currentPath === '/anomaly-dashboards/swchange-dashboard'}>
            <Link to="/anomaly-dashboards/swchange-dashboard">
              <span>SW Change Dashboard</span>
            </Link>
          </ReactEDSWrapper>
          <ReactEDSWrapper component={NavItem} active={currentPath === '/anomaly-dashboards/cd-monitoring-viewer'}>
            <Link to="/anomaly-dashboards/cd-monitoring-viewer">
              <span>Pilot Monitoring Viewer</span>
            </Link>
          </ReactEDSWrapper>
          <ReactEDSWrapper component={NavItem} active={currentPath === '/anomaly-dashboard/anomaly-viewer'}>
            <Link to="/anomaly-dashboard/anomaly-viewer">
              <span>Anomaly Viewer</span>
            </Link>
          </ReactEDSWrapper>
          <ReactEDSWrapper component={NavItem} active={currentPath === '/anomaly-dashboard/interference-viewer'}>
            <Link to="/anomaly-dashboard/interference-viewer">
              <span>Interference Viewer</span>
            </Link>
          </ReactEDSWrapper>
          <ReactEDSWrapper component={NavItem} active={currentPath === '/anomaly-dashboard/moi-comparison'}>
            <Link to="/anomaly-dashboard/moi-comparison">
              <span>MOI Comparison</span>
            </Link>
          </ReactEDSWrapper>
          <ReactEDSWrapper component={NavItem} active={currentPath === '/anomaly-dashboard/system-constant-viewer'}>
            <Link to="/anomaly-dashboard/system-constant-viewer">
              <span>System Constant Viewer</span>
            </Link>
          </ReactEDSWrapper>
          <ReactEDSWrapper component={NavItem} active={currentPath === '/anomaly-dashboard/system-information'}>
            <Link to="/anomaly-dashboard/system-information">
              <span>System Information</span>
            </Link>
          </ReactEDSWrapper>
        </ReactEDSWrapper>
        {/* Advanced Configuration - restored full nested tree.
              NOTE: "Dashboards" and "Health Check" sub-menus are intentionally
              excluded (removed 2026-05-31, docs/advanced-config-menu-fixes-2026-05-31.md)
              because their backend modules (NIA BI dashboards / Netdata) are not
              deployed in this stack. Re-add them only when those backends exist.
              Each leaf item is gated by its own NIA permission via `style`. */}
        <ReactEDSWrapper
          component={NavGroup}
          title="Advanced Configuration"
          active={currentPath.startsWith('/advanced-config') || currentPath === '/system-dashboard'}
        >
          {/* Administration — hidden under Keycloak auth (managed in Keycloak) */}
          {!hideNiaAdministration && (
            <ReactEDSWrapper component={NavGroup} title="Administration" active={currentPath.startsWith('/advanced-config/administration')}>
              <ReactEDSWrapper
                component={NavItem}
                active={currentPath === '/advanced-config/administration/users'}
                style={hasNiaPermission(NIA_PERMISSIONS.ADMINISTRATION.USERS) ? {} : { display: 'none' }}
              >
                <Link to="/advanced-config/administration/users">
                  <span>Users</span>
                </Link>
              </ReactEDSWrapper>
              <ReactEDSWrapper
                component={NavItem}
                active={currentPath === '/advanced-config/administration/roles'}
                style={hasNiaPermission(NIA_PERMISSIONS.ADMINISTRATION.ROLES) ? {} : { display: 'none' }}
              >
                <Link to="/advanced-config/administration/roles">
                  <span>Roles</span>
                </Link>
              </ReactEDSWrapper>
              <ReactEDSWrapper
                component={NavItem}
                active={currentPath === '/advanced-config/administration/permissions'}
                style={hasNiaPermission(NIA_PERMISSIONS.ADMINISTRATION.PERMISSIONS) ? {} : { display: 'none' }}
              >
                <Link to="/advanced-config/administration/permissions">
                  <span>Permissions</span>
                </Link>
              </ReactEDSWrapper>
              <ReactEDSWrapper
                component={NavItem}
                active={currentPath === '/advanced-config/administration/user-activities'}
                style={hasNiaPermission(NIA_PERMISSIONS.ADMINISTRATION.ROLES) ? {} : { display: 'none' }}
              >
                <Link to="/advanced-config/administration/user-activities">
                  <span>User Activities</span>
                </Link>
              </ReactEDSWrapper>
              <ReactEDSWrapper
                component={NavItem}
                active={currentPath === '/advanced-config/administration/features'}
                style={hasNiaPermission(NIA_PERMISSIONS.ADMINISTRATION.FEATURES) ? {} : { display: 'none' }}
              >
                <Link to="/advanced-config/administration/features">
                  <span>Features</span>
                </Link>
              </ReactEDSWrapper>
            </ReactEDSWrapper>
          )}
          {/* Use Cases */}
          <ReactEDSWrapper component={NavGroup} title="Use Cases" active={currentPath.startsWith('/advanced-config/use-cases')}>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/use-cases/templates'}
              style={hasNiaPermission(NIA_PERMISSIONS.USE_CASES.TEMPLATES) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/use-cases/templates">
                <span>Templates</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/use-cases/instances'}
              style={hasNiaPermission(NIA_PERMISSIONS.USE_CASES.INSTANCES) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/use-cases/instances">
                <span>Instances</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/use-cases/schedules'}
              style={hasNiaPermission(NIA_PERMISSIONS.USE_CASES.SCHEDULES) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/use-cases/schedules">
                <span>Schedules</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/use-cases/subscriptions'}
              style={hasNiaPermission(NIA_PERMISSIONS.USE_CASES.SUBSCRIPTIONS) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/use-cases/subscriptions">
                <span>Subscriptions</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/use-cases/executions'}
              style={hasNiaPermission(NIA_PERMISSIONS.USE_CASES.EXECUTIONS) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/use-cases/executions">
                <span>Executions</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/use-cases/exclusions'}
              style={hasNiaPermission(NIA_PERMISSIONS.USE_CASES.EXCLUSIONS) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/use-cases/exclusions">
                <span>Exclusions</span>
              </Link>
            </ReactEDSWrapper>
          </ReactEDSWrapper>
          {/* Recovery Actions */}
          <ReactEDSWrapper
            component={NavGroup}
            title="Recovery Actions"
            active={currentPath.startsWith('/advanced-config/recovery-actions')}
          >
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/recovery-actions/templates'}
              style={hasNiaPermission(NIA_PERMISSIONS.RECOVERY_ACTIONS.TEMPLATES) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/recovery-actions/templates">
                <span>Templates</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/recovery-actions/instances'}
              style={hasNiaPermission(NIA_PERMISSIONS.RECOVERY_ACTIONS.INSTANCES) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/recovery-actions/instances">
                <span>Instances</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/recovery-actions/executions'}
              style={hasNiaPermission(NIA_PERMISSIONS.RECOVERY_ACTIONS.EXECUTIONS) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/recovery-actions/executions">
                <span>Executions</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/recovery-actions/results'}
              style={hasNiaPermission(NIA_PERMISSIONS.RECOVERY_ACTIONS.RESULTS) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/recovery-actions/results">
                <span>Results</span>
              </Link>
            </ReactEDSWrapper>
          </ReactEDSWrapper>
          {/* Reporters */}
          <ReactEDSWrapper component={NavGroup} title="Reporters" active={currentPath.startsWith('/advanced-config/reporters')}>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/reporters/templates'}
              style={hasNiaPermission(NIA_PERMISSIONS.REPORTERS.TEMPLATES) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/reporters/templates">
                <span>Templates</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/reporters/instances'}
              style={hasNiaPermission(NIA_PERMISSIONS.REPORTERS.INSTANCES) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/reporters/instances">
                <span>Instances</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/reporters/executions'}
              style={hasNiaPermission(NIA_PERMISSIONS.REPORTERS.EXECUTIONS) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/reporters/executions">
                <span>Executions</span>
              </Link>
            </ReactEDSWrapper>
          </ReactEDSWrapper>
          {/* Results */}
          <ReactEDSWrapper
            component={NavItem}
            active={currentPath === '/advanced-config/results'}
            style={hasNiaPermission(NIA_PERMISSIONS.RESULTS) ? {} : { display: 'none' }}
          >
            <Link to="/advanced-config/results">
              <span>Result List</span>
            </Link>
          </ReactEDSWrapper>
          {/* KPI Definitions */}
          <ReactEDSWrapper
            component={NavItem}
            active={currentPath === '/advanced-config/kpi-definitions'}
            style={hasNiaPermission(NIA_PERMISSIONS.KPI_DEFINITIONS) ? {} : { display: 'none' }}
          >
            <Link to="/advanced-config/kpi-definitions">
              <span>KPI Definitions List</span>
            </Link>
          </ReactEDSWrapper>
          {/* Global Store */}
          <ReactEDSWrapper
            component={NavItem}
            active={currentPath === '/advanced-config/global-store'}
            style={hasNiaPermission(NIA_PERMISSIONS.GLOBAL_STORE) ? {} : { display: 'none' }}
          >
            <Link to="/advanced-config/global-store">
              <span>Global Store</span>
            </Link>
          </ReactEDSWrapper>
          {/* ENI Value Portal */}
          <ReactEDSWrapper component={NavGroup} title="ENI Value Portal" active={currentPath.startsWith('/advanced-config/value')}>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/value/upload'}
              style={hasNiaPermission(NIA_PERMISSIONS.VALUE) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/value/upload">
                <span>Upload File</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/value/weekly-achievement'}
              style={hasNiaPermission(NIA_PERMISSIONS.VALUE) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/value/weekly-achievement">
                <span>Weekly Achievement</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/value/nff-recommendation'}
              style={hasNiaPermission(NIA_PERMISSIONS.VALUE) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/value/nff-recommendation">
                <span>NFF Recommendation</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/value/nff-prevented'}
              style={hasNiaPermission(NIA_PERMISSIONS.VALUE) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/value/nff-prevented">
                <span>NFF Prevented</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/value/silent-issue'}
              style={hasNiaPermission(NIA_PERMISSIONS.VALUE) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/value/silent-issue">
                <span>Silent Issue</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/value/near-silent-issue'}
              style={hasNiaPermission(NIA_PERMISSIONS.VALUE) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/value/near-silent-issue">
                <span>Near Silent Issue</span>
              </Link>
            </ReactEDSWrapper>
          </ReactEDSWrapper>
          {/* Clusters */}
          <ReactEDSWrapper
            component={NavItem}
            active={currentPath === '/advanced-config/clusters'}
            style={hasNiaPermission(NIA_PERMISSIONS.CLUSTER) ? {} : { display: 'none' }}
          >
            <Link to="/advanced-config/clusters">
              <span>Clusters</span>
            </Link>
          </ReactEDSWrapper>
          {/* EPC */}
          <ReactEDSWrapper component={NavGroup} title="EPC" active={currentPath.startsWith('/advanced-config/epc')}>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/epc/nodes'}
              style={hasNiaPermission(NIA_PERMISSIONS.EPC.NODES) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/epc/nodes">
                <span>Nodes</span>
              </Link>
            </ReactEDSWrapper>
            <ReactEDSWrapper
              component={NavItem}
              active={currentPath === '/advanced-config/epc/full-cm'}
              style={hasNiaPermission(NIA_PERMISSIONS.EPC.CM) ? {} : { display: 'none' }}
            >
              <Link to="/advanced-config/epc/full-cm">
                <span>Full CM</span>
              </Link>
            </ReactEDSWrapper>
          </ReactEDSWrapper>
          {/* Feedback */}
          <ReactEDSWrapper
            component={NavItem}
            active={currentPath === '/advanced-config/feedback'}
            style={hasNiaPermission(NIA_PERMISSIONS.FEEDBACK) ? {} : { display: 'none' }}
          >
            <Link to="/advanced-config/feedback">
              <span>Feedback</span>
            </Link>
          </ReactEDSWrapper>
        </ReactEDSWrapper>
        <ReactEDSWrapper component={NavItem} active={currentPath.startsWith('/dcs-settings')}>
          <Link to="/dcs-settings">
            <span>Settings</span>
          </Link>
        </ReactEDSWrapper>
      </ReactEDSWrapper>

      {/* TraceHunt */}
      {isShowTraceHunt && (
        <ReactEDSWrapper component={NavItem} active={currentPath.startsWith('/tracehunt')}>
          <Link to="/tracehunt">
            <span>TraceHunt</span>
          </Link>
        </ReactEDSWrapper>
      )}

      {/* Settings - Admin only */}
      {hasRole(UserRoles.Admin) && (
        <ReactEDSWrapper component={NavItem} active={currentPath === '/settings'}>
          <Link to="/settings">
            <span>Settings</span>
          </Link>
        </ReactEDSWrapper>
      )}
    </ReactEDSWrapper>
  );

  const signOut = () => {
    setShowSystemPanel(false);
    secureLocalStorage.removeItem('hasShownLoginNotification');
    logout();
  };

  const findMatchingUrl = (text: string) => {
    for (const [key, value] of Object.entries(pathToTitleMap)) {
      const match = RegExp(key).exec(text);
      if (match) return value;
    }
    return null;
  };

  const notificationPanel = (
    <ReactEDSWrapper
      component={NotificationLogPanel}
      onClose={() => {
        setShowNotificationPanel(false);
        readNotification();
      }}
    >
      {notifications.map((item) => (
        <ReactEDSWrapper
          key={item.title}
          component={NotificationLogItem}
          isNew={item.viewed}
          title={item.title}
          description={item.description}
          timestamp={item.timestamp}
        />
      ))}
    </ReactEDSWrapper>
  );

  return (
    <div className={`fade-in ${loaded ? 'loaded' : ''}`}>
      <ReactEDSWrapper component={SystemBar} product="RAN Life Cycle Automation" acronym="RLCA">
        <>
          <ReactEDSWrapper component={SystemBarComponent} className="version-number-item">
            <span id="eds-version">v26.3.0</span>
          </ReactEDSWrapper>
          <ReactEDSWrapper
            component={NotificationTrigger}
            notifications={newNotifications}
            onClick={() => {
              acknowledgeNotification();
              setShowNotificationPanel(!showNotificationPanel);
            }}
          />
          <ReactEDSWrapper
            component={SystemBarComponent}
            onClick={() => {
              setShowNotificationPanel(false);
              setShowSystemPanel(!showSystemPanel);
            }}
          >
            <i className="icon icon-profile" />
            <span className="user">{user?.username}</span>
          </ReactEDSWrapper>
        </>
      </ReactEDSWrapper>
      <ReactEDSWrapper
        component={AppContainer}
        hideNavigation={false}
        systemPanels={[
          showNotificationPanel ? notificationPanel : <></>,
          showSystemPanel ? (
            <SystemPanel
              user={{
                displayName: user?.displayName || '',
                email: user?.email || '',
                expiryTime: 0,
                username: user?.username || '',
                roles: user?.roles || [],
                lastLoginAt
              }}
              theme={theme}
              toggleTheme={toggleTheme}
              signOut={signOut}
              onRequestClose={() => setShowSystemPanel(false)}
              silentMode={silentMode}
              setSilentMode={setSilentMode}
            />
          ) : (
            <></>
          )
        ]}
        showSystemPanel={showSystemPanel || showNotificationPanel}
      >
        <ReactEDSWrapper component={AppBar}>{findMatchingUrl(currentPath)}</ReactEDSWrapper>
        <ReactEDSWrapper component={AppBody} navigation={navigation}>
          <Outlet />
        </ReactEDSWrapper>
      </ReactEDSWrapper>
    </div>
  );
};

export default ProtectedRoute;
